import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { dequeueBet, pendingBets, type QueuedBet } from '../lib/betQueue';
import { ApiError } from '../services/api-client';
import { marketsService } from '../services/markets.service';

/**
 * Replays bets that never got confirmed, on reconnect and on load.
 *
 * A 4xx means the server considered and rejected it -- the market closed, the
 * balance was short, the cap was reached -- so the entry is dropped rather than
 * retried forever. Only a network failure or a server fault is worth another
 * attempt.
 */
export const useBetQueue = () => {
    const queryClient = useQueryClient();
    const [pending, setPending] = useState<QueuedBet[]>(() => pendingBets());
    const [flushing, setFlushing] = useState(false);

    const flush = useCallback(async () => {
        const queue = pendingBets();
        setPending(queue);
        if (queue.length === 0 || !navigator.onLine) return;

        setFlushing(true);
        let settled = 0;

        for (const bet of queue) {
            try {
                await marketsService.bet(bet);
                dequeueBet(bet.idempotencyKey);
                settled += 1;
            } catch (err) {
                if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
                    dequeueBet(bet.idempotencyKey);
                    settled += 1;
                }
                // Anything else stays queued for the next attempt.
            }
        }

        setPending(pendingBets());
        setFlushing(false);

        if (settled > 0) {
            void queryClient.invalidateQueries({ queryKey: ['markets'] });
            void queryClient.invalidateQueries({ queryKey: ['hangouts'] });
        }
    }, [queryClient]);

    useEffect(() => {
        void flush();
        const onOnline = (): void => void flush();
        window.addEventListener('online', onOnline);
        return () => window.removeEventListener('online', onOnline);
    }, [flush]);

    return { pending, flushing, flush, refresh: () => setPending(pendingBets()) };
};
